import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { BarChart3, Calendar, TrendingUp, PieChart, LogOut, Settings, Users } from 'lucide-react';
import GoogleSignIn from './signin';
import Page1 from './pages/Page1';
import Page2 from './pages/Page2';
import Page3 from './pages/Page3';
import Page4 from './pages/Page4';
import Page4Updated from './pages/Page4Updated';
 
export default function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}
 
function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
 
  const handleAuthSuccess = (user) => {
    setAuthenticated(true);
    navigate('/'); // Redirect to Sales Report page (or any page)
  };
 
  const handleLogout = () => {
    setAuthenticated(false);
    navigate('/');
  };
 
  if (!authenticated) {
    return <GoogleSignIn onSuccess={handleAuthSuccess} />;
  }
 
  const navigationItems = [
    {
      path: '/',
      label: 'Sales Analytics',
      icon: BarChart3,
      description: 'Comprehensive sales performance'
    },
    {
      path: '/page2',
      label: 'Budget Analysis',
      icon: Calendar,
      description: 'Budget vs actual by date range'
    },
    {
      path: '/page3',
      label: 'Budget Trends',
      icon: TrendingUp,
      description: 'Visual budget performance'
    },
    {
      path: '/page4',
      label: 'Sales By End User',
      icon: Users,
      description: 'Sales by end user'
    },
    {
      path: '/page4-editable',
      label: 'Budget Management',
      icon: PieChart,
      description: 'Edit and manage budget data records'
    }
  ];
 
  const isActive = (path) => location.pathname === path;
 
  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Enhanced Professional Navigation */}
      <nav className="w-72 bg-white/95 backdrop-blur-sm border-r border-gray-200 shadow-xl fixed left-0 top-0 bottom-0 z-30 overflow-hidden">
        {/* Brand Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 p-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-white rounded-xl backdrop-blur-sm">
                <img
                  src="/CODiLogo.webp"
                  alt="CODi Logo"
                  className="h-8 w-8 object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">NetSuite Analytics</h1>
                <p className="text-indigo-100 text-sm">Business Intelligence Suite</p>
              </div>
            </div>
          </div>
          {/* Decorative gradient overlay */}
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
        </div>
 
        {/* Navigation Menu */}
        <div className="p-6 space-y-2">
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Analytics Dashboard</p>
          </div>
         
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
           
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center p-4 rounded-xl transition-all duration-200 relative overflow-hidden ${
                  active
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25 transform scale-[1.02]'
                    : 'text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-700 hover:shadow-md hover:transform hover:scale-[1.01]'
                }`}
              >
                {/* Active indicator */}
                {active && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-full"></div>
                )}
               
                {/* Icon container */}
                <div className={`p-2 rounded-lg mr-4 transition-all duration-200 ${
                  active
                    ? 'bg-white/20 shadow-lg'
                    : 'bg-gray-100 group-hover:bg-indigo-100 group-hover:shadow-md'
                }`}>
                  <Icon className={`h-5 w-5 transition-all duration-200 ${
                    active ? 'text-white' : 'text-gray-600 group-hover:text-indigo-600'
                  }`} />
                </div>
               
                {/* Text content */}
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold transition-all duration-200 ${
                    active ? 'text-white' : 'text-gray-800 group-hover:text-indigo-800'
                  }`}>
                    {item.label}
                  </p>
                  <p className={`text-sm transition-all duration-200 truncate ${
                    active ? 'text-indigo-100' : 'text-gray-500 group-hover:text-indigo-600'
                  }`}>
                    {item.description}
                  </p>
                </div>
 
                {/* Hover indicator */}
                {!active && (
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
 
        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-gray-50 to-transparent">
          <div className="space-y-2">
            {/* Settings link */}
            <button className="group w-full flex items-center p-3 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200">
              <div className="p-2 bg-gray-100 group-hover:bg-indigo-100 rounded-lg mr-3 transition-all duration-200">
                <Settings className="h-4 w-4 transition-all duration-200" />
              </div>
              <span className="font-medium">Settings</span>
            </button>
           
            {/* Logout button */}
            <button
              onClick={handleLogout}
              className="group w-full flex items-center p-3 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
            >
              <div className="p-2 bg-gray-100 group-hover:bg-red-100 rounded-lg mr-3 transition-all duration-200">
                <LogOut className="h-4 w-4 transition-all duration-200" />
              </div>
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
         
          {/* User info section */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">U</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">User Account</p>
                <p className="text-xs text-gray-500">Analytics Dashboard</p>
              </div>
            </div>
          </div>
        </div>
      </nav>
 
      {/* Main Content Area */}
      <div className="flex-1 ml-72 min-h-screen">
        <Routes>
          <Route path="/" element={<Page1 />} />
          <Route path="/page2" element={<Page2 />} />
          <Route path="/page3" element={<Page3 />} />
          <Route path="/page4" element={<Page4Updated />} />
          <Route path="/page4-editable" element={<Page4 />} />
        </Routes>
      </div>
    </div>
  );
}
 
 